/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd and/or Orbit Tickets Ltd. All rights reserved.
This file contains a combination of:
- Protected Intellectual Property owned by Kaleida Ltd; and
- Custom / Included Intellectual Property owned by the Customer,
and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (being any application not developed by Kaleida), whether or not such application forms part of the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, where separation between Protected Intellectual Property and Custom / Included Intellectual Property is unclear, this file shall be treated as containing Protected Intellectual Property.
*/

// IP-NOTICE: MIXED
import { ComponentFactoryResolver, ElementRef, EventEmitter, Injectable, Injector } from '@angular/core';
import { DialogPosition, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ComponentItem } from 'app/shared/base/aside/component-item';
import { AsideModalContainerComponent } from '../aside-modal-container/aside-modal-container.component';

@Injectable({ providedIn: 'root' })
export class AsideService {
  testVal: number;
  asideOpen = new EventEmitter<void>();
  asideClose = new EventEmitter<any>();
  componentProvided: EventEmitter<ComponentItem> = new EventEmitter();
  componentUnset: EventEmitter<boolean> = new EventEmitter();
  public isOpen = false;

  constructor(private dialog: MatDialog = null) {}

  public open(): void {
    this.isOpen = true;
    this.asideOpen.emit();
  }

  public close(data?: any): void {
    this.isOpen = false;
    this.unsetComponent();
    if (this.dialog) this.dialog.closeAll();
    this.asideClose.emit(data);
  }

  setComponent(componentItem: ComponentItem) {
    this.componentProvided.emit(componentItem);
  }

  unsetComponent() {
    this.componentUnset.emit(true);
  }

  // https://www.reddit.com/r/Angular2/comments/6dij2p/passing_injector_to_createcomponent/
  setAndOpen(componentItem: ComponentItem, injector: Injector) {
    const resolver = injector.get(ComponentFactoryResolver);
    componentItem.resolver = resolver;
    this.setComponent(componentItem);
    this.open();
  }

  openAsideDialog(
    component: any,
    inputs: any,
    width: string = 'calc(100vw - 12px)',
    height: string = 'auto',
    position: Partial<DialogPosition> = { bottom: '0', right: '0' },
    maxWidth: string = '80vw'
  ): MatDialogRef<AsideModalContainerComponent> {
    return this.dialog.open(AsideModalContainerComponent, {
      data: { component, inputs },
      width,
      maxWidth,
      height,
      position,
      disableClose: true,
      panelClass: 'aside-dialog-panel',
      enterAnimationDuration: '0ms',
      exitAnimationDuration: '0ms',
    });
  }

  openAsideV2Dialog(
    component: any,
    inputs: any,
    width: string = 'calc(100vw - 48px)',
    height: string = 'calc(100vh - 48px)',
    position: Partial<DialogPosition> = { bottom: '24px', right: '24px' },
    maxWidth: string = '80vw'
  ): MatDialogRef<AsideModalContainerComponent> {
    return this.dialog.open(AsideModalContainerComponent, {
      data: { component, inputs },
      width,
      maxWidth,
      height,
      maxHeight: 'calc(100vh - 48px)',
      position,
      disableClose: true,
      panelClass: ['aside-dialog-panel', 'aside-dialog-panel--v2'],
      enterAnimationDuration: '0ms',
      exitAnimationDuration: '0ms',
    });
  }

  openAsideDialogOverCard(component: any, inputs: any, cardElement: ElementRef, heightOffset = 0, fitToViewport = false, useV2Panel = false): MatDialogRef<AsideModalContainerComponent> {
    const rect = cardElement.nativeElement.getBoundingClientRect();
    const height = fitToViewport ? Math.max(0, window.innerHeight - rect.top - heightOffset) : rect.height - heightOffset;
    const position = {
      top: `${rect.top + window.scrollY}px`,
      left: `${rect.left + window.scrollX}px`,
    };
    const dialogRef = useV2Panel
      ? this.openAsideV2Dialog(component, inputs, `${rect.width}px`, `${height}px`, position, '100vw')
      : this.openAsideDialog(component, inputs, `${rect.width}px`, `${height}px`, position, '100vw');

    let resizeFrame: number | null = null;
    const updateDialogBounds = () => {
      resizeFrame = null;
      const currentRect = cardElement.nativeElement.getBoundingClientRect();
      const currentHeight = fitToViewport ? Math.max(0, window.innerHeight - currentRect.top - heightOffset) : currentRect.height - heightOffset;

      dialogRef.updateSize(`${currentRect.width}px`, `${currentHeight}px`);
      dialogRef.updatePosition({
        top: `${currentRect.top + window.scrollY}px`,
        left: `${currentRect.left + window.scrollX}px`,
      });
    };
    const scheduleDialogBoundsUpdate = () => {
      if (resizeFrame !== null) {
        cancelAnimationFrame(resizeFrame);
      }
      resizeFrame = requestAnimationFrame(updateDialogBounds);
    };

    window.addEventListener('resize', scheduleDialogBoundsUpdate, { passive: true });
    const cardResizeObserver = new ResizeObserver(scheduleDialogBoundsUpdate);
    cardResizeObserver.observe(cardElement.nativeElement);

    dialogRef.afterClosed().subscribe(() => {
      window.removeEventListener('resize', scheduleDialogBoundsUpdate);
      cardResizeObserver.disconnect();
      if (resizeFrame !== null) {
        cancelAnimationFrame(resizeFrame);
      }
    });

    return dialogRef;
  }
}
